import React, { useState } from 'react';
import {
  Box,
  Chip,
  Collapse,
  IconButton,
  LinearProgress,
  Paper,
  Tooltip,
  Typography,
} from '@mui/material';
import { Activity, AlertTriangle, ChevronDown, ChevronUp, Database, HardDrive } from 'lucide-react';
import { useTelemetry } from '../api';

const STATUS_COLOR: Record<string, 'success' | 'warning' | 'error'> = {
  ok: 'success',
  stale: 'warning',
  dead: 'error',
};

export default function TelemetryPanel() {
  const { data, isLoading } = useTelemetry();
  const [open, setOpen] = useState(true);

  if (isLoading || !data) {
    return <LinearProgress sx={{ mb: 2, borderRadius: 1 }} />;
  }

  const totalGaps = Object.values(data.pipeline.gaps).reduce((s, g) => s + g.length, 0);

  return (
    <Paper
      elevation={0}
      sx={{
        mb: 2,
        p: 1.5,
        backdropFilter: 'blur(16px)',
        background: (t) =>
          t.palette.mode === 'dark' ? 'rgba(15,23,42,0.6)' : 'rgba(248,250,252,0.7)',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 3,
      }}
    >
      {/* Header */}
      <Box display="flex" alignItems="center" justifyContent="space-between">
        <Box display="flex" alignItems="center" gap={1.5}>
          <Activity size={18} />
          <Typography variant="subtitle2" fontWeight={600}>
            Pipeline Telemetry
          </Typography>
          {totalGaps > 0 && (
            <Chip
              icon={<AlertTriangle size={14} />}
              label={`${totalGaps} gap${totalGaps > 1 ? 's' : ''}`}
              size="small"
              color="warning"
              variant="outlined"
            />
          )}
        </Box>
        <IconButton size="small" onClick={() => setOpen(!open)}>
          {open ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
        </IconButton>
      </Box>

      <Collapse in={open}>
        <Box display="flex" gap={2} mt={1.5} flexWrap="wrap">
          {/* ETL Steps */}
          <MetricCard title="ETL Steps" icon={<Activity size={16} />}>
            <Box display="flex" gap={0.5} flexWrap="wrap">
              {Object.entries(data.etl_steps).map(([name, step]) => (
                <Tooltip key={name} title={`Last: ${step.last_run} (${step.age_hours}h ago)`}>
                  <Chip
                    label={name}
                    size="small"
                    color={STATUS_COLOR[step.status] || 'default'}
                    variant="filled"
                    sx={{ fontSize: 11 }}
                  />
                </Tooltip>
              ))}
              {Object.keys(data.etl_steps).length === 0 && (
                <Typography variant="caption" color="text.secondary">
                  No ETL metrics yet
                </Typography>
              )}
            </Box>
          </MetricCard>

          {/* Data Gaps */}
          <MetricCard title="Data Gaps (90d)" icon={<AlertTriangle size={16} />}>
            {totalGaps === 0 ? (
              <Chip label="No gaps" size="small" color="success" variant="outlined" />
            ) : (
              <Box display="flex" gap={0.5} flexWrap="wrap" maxHeight={60} overflow="auto">
                {Object.entries(data.pipeline.gaps).map(([seg, dates]) =>
                  dates.slice(0, 5).map((d) => (
                    <Chip key={`${seg}-${d}`} label={`${seg.slice(4)} ${d.slice(5)}`} size="small" color="error" variant="outlined" sx={{ fontSize: 10 }} />
                  )),
                )}
              </Box>
            )}
          </MetricCard>

          {/* Ledger */}
          <MetricCard title="Ledger" icon={<Database size={16} />}>
            <Box display="flex" gap={0.5}>
              {Object.entries(data.ledger).map(([status, count]) => (
                <Chip
                  key={status}
                  label={`${status}: ${count}`}
                  size="small"
                  color={status === 'failed' ? 'error' : status === 'needs_recheck' ? 'warning' : 'success'}
                  variant="outlined"
                  sx={{ fontSize: 11 }}
                />
              ))}
            </Box>
          </MetricCard>

          {/* Disk */}
          <MetricCard title="Disk" icon={<HardDrive size={16} />}>
            <Box>
              <LinearProgress
                variant="determinate"
                value={(data.disk.used_gb / data.disk.total_gb) * 100}
                sx={{ height: 8, borderRadius: 4, mb: 0.5 }}
              />
              <Typography variant="caption" color="text.secondary">
                {data.disk.used_gb} / {data.disk.total_gb} GB
                {' · '}
                {Object.entries(data.disk.by_layer_gb)
                  .map(([l, gb]) => `${l}: ${gb}G`)
                  .join(' · ')}
              </Typography>
            </Box>
          </MetricCard>
        </Box>
      </Collapse>
    </Paper>
  );
}

function MetricCard({
  title,
  icon,
  children,
}: {
  title: string;
  icon: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <Paper
      elevation={0}
      sx={{
        flex: '1 1 220px',
        minWidth: 200,
        p: 1.5,
        borderRadius: 2,
        background: (t) =>
          t.palette.mode === 'dark' ? 'rgba(30,41,59,0.5)' : 'rgba(241,245,249,0.8)',
        border: '1px solid',
        borderColor: 'divider',
      }}
    >
      <Box display="flex" alignItems="center" gap={0.5} mb={1}>
        {icon}
        <Typography variant="caption" fontWeight={600} textTransform="uppercase" letterSpacing={0.5}>
          {title}
        </Typography>
      </Box>
      {children}
    </Paper>
  );
}
